import React, { useState, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Company } from "../models/Company";
import {
  Box,
  Typography,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  FormHelperText,
  Link,
} from "@mui/material";
import { Upload, Download } from "@mui/icons-material";
import { createUser, InviteUserResult } from "../services/userService";
import { CsvUserRow, parseCsvUsers } from "../utils/csvParser";
import { User } from "../models/User";

interface BatchImportUsersDialogProps {
  preSelectedCompanyId?: number;
  companies: Company[];
  onSuccess: (createdUsers?: User[]) => void;  // Updated signature
}

type RowStatus = "pending" | "importing" | "success" | "error";

interface ImportRow extends CsvUserRow {
  status: RowStatus;
  errorMessage?: string;
  inviteResult?: InviteUserResult;
}

const BatchImportUsersDialog: React.FC<BatchImportUsersDialogProps> = ({
  preSelectedCompanyId,
  companies,
  onSuccess,
}) => {
  const { getAccessToken, userName } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [companyId, setCompanyId] = useState<number | undefined>(preSelectedCompanyId);
  const [entraUser, setEntraUser] = useState(true);
  const [entraAdmin, setEntraAdmin] = useState(false);
  const [entraReader, setEntraReader] = useState(false);
  const [sendInvite, setSendInvite] = useState(false);

  const [rows, setRows] = useState<ImportRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const createdUsersRef = useRef<User[]>([]);

  const selectedCompany = companies.find((c) => c.companyId === companyId);

  // Build group list based on checkboxes
  const buildGroupList = (): string[] => {
    if (!selectedCompany) return [];
    const groups: string[] = [];
    if (entraUser && selectedCompany.userGroupObjectId) groups.push(selectedCompany.userGroupObjectId);
    if (entraAdmin && selectedCompany.adminGroupObjectId) groups.push(selectedCompany.adminGroupObjectId);
    if (entraReader && selectedCompany.resultGroupObjectId) groups.push(selectedCompany.resultGroupObjectId);
    return groups;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParseError(null);
    setImportDone(false);
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const csvText = evt.target?.result as string;
        const parsed = parseCsvUsers(csvText);
        setRows(parsed.map((r) => ({ ...r, status: "pending" as RowStatus })));
      } catch (err: any) {
        setParseError(err.message || "Failed to parse CSV file");
        setRows([]);
      }
    };
    reader.readAsText(file);

    // Reset file input so re-uploading same file triggers change
    e.target.value = "";
  };

  const handleRemoveRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImport = async () => {
    if (!selectedCompany) return;

    const groupsToAdd = buildGroupList();
    if (groupsToAdd.length === 0) {
      setParseError("No valid Entra groups found. Please select at least one role.");
      return;
    }

    setImporting(true);
    setImportDone(false);
    setParseError(null);

    const updatedRows = [...rows];

    // Collect results for all user creations
    const results = await Promise.all(
      updatedRows.map(async (row, i): Promise<{ ok: boolean; data?: User; error?: string } | null> => {
        if (row.status === "success") return null; // Skip already imported

        // Mark as importing
        updatedRows[i] = { ...row, status: "importing" };
        setRows([...updatedRows]);

        try {
          const user = {
            email: row.email,
            userName: row.displayName,
            company: selectedCompany.companyName || "",
            companyId: selectedCompany.companyId,
            adminUserId: 0,
            entraUser,
            entraAdmin,
            entraReader,
          };

          const result = await createUser(user, userName || "", groupsToAdd, getAccessToken, sendInvite);

          if (result.ok) {
            updatedRows[i] = { ...row, status: "success", inviteResult: result.data };
          } else {
            updatedRows[i] = { ...row, status: "error", errorMessage: result.error || "Failed" };
          }

          setRows([...updatedRows]);
          return result;
        } catch (err: any) {
          updatedRows[i] = { ...row, status: "error", errorMessage: err.message || "Unexpected error" };
          setRows([...updatedRows]);
          return { ok: false, error: err.message || "Unexpected error" };
        }
      })
    );

    setImporting(false);
    setImportDone(true);

    // After all batch invites complete, collect the successful users from the response wrapper:
    const createdUsers = results
      .filter((r): r is { ok: true; data: InviteUserResult } => !!r && r.ok === true && !!r.data)
      .map((r) => r.data.user);
    
    // Accumulate (in case of retries) instead of calling onSuccess immediately
    createdUsersRef.current = [...createdUsersRef.current, ...createdUsers];
  };

  const handleDone = useCallback(() => {
    onSuccess(createdUsersRef.current.length > 0 ? createdUsersRef.current : undefined);
  }, [onSuccess]);

  const successCount = rows.filter((r) => r.status === "success").length;
  const errorCount = rows.filter((r) => r.status === "error").length;
  const pendingCount = rows.filter((r) => r.status === "pending").length;

  const handleDownloadTemplate = () => {
    const csv = "Email,Name\njohn@example.com,John Doe\njane@example.com,Jane Doe\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "user_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a CSV file with <strong>Email</strong> and <strong>Name</strong> columns to invite multiple users at once.
      </Typography>

      {/* Company selector */}
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel id="batch-company-label">Company</InputLabel>
        <Select
          labelId="batch-company-label"
          value={companyId || ""}
          label="Company"
          onChange={(e) => setCompanyId(Number(e.target.value))}
        >
          <MenuItem value="">
            <em>Select a company</em>
          </MenuItem>
          {companies.map((c) => (
            <MenuItem key={c.companyId} value={c.companyId}>
              {c.companyName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Role checkboxes */}
      <Box sx={{ display: "flex", gap: 2, mt: 1, mb: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={entraUser} onChange={(e) => setEntraUser(e.target.checked)} color="primary" />}
          label="Entra User"
        />
        <FormControlLabel
          control={<Checkbox checked={entraAdmin} onChange={(e) => setEntraAdmin(e.target.checked)} color="primary" />}
          label="Entra Admin"
        />
        <FormControlLabel
          control={<Checkbox checked={entraReader} onChange={(e) => setEntraReader(e.target.checked)} color="primary" />}
          label="Entra Reader"
        />
      </Box>

      {/* Send invite checkbox */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} color="primary" />}
          label="Send invite email"
        />
      </Box>

      {/* CSV file upload */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<Upload />}
          onClick={() => fileInputRef.current?.click()}
          disabled={importing}
        >
          Upload CSV
        </Button>
        <Link
          component="button"
          variant="body2"
          onClick={handleDownloadTemplate}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <Download fontSize="small" /> Download template
        </Link>
        <input ref={fileInputRef} type="file" accept=".csv" hidden onChange={handleFileChange} />
      </Box>

      {parseError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setParseError(null)}>
          {parseError}
        </Alert>
      )}

      {/* Preview table */}
      {rows.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {rows.length} user{rows.length !== 1 ? "s" : ""} loaded
            {importDone && (
              <span>
                {" "}— {successCount} succeeded, {errorCount} failed
              </span>
            )}
          </Typography>

          {importing && <LinearProgress sx={{ mb: 1 }} />}

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, mb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.displayName}</TableCell>
                    <TableCell align="center">
                      {row.status === "pending" && <Chip label="Pending" size="small" />}
                      {row.status === "importing" && <Chip label="Importing…" size="small" color="info" />}
                      {row.status === "success" && (
                        <Chip
                          label={row.inviteResult?.user?.status || "Success"}
                          size="small"
                          color="success"
                        />
                      )}
                      {row.status === "error" && (
                        <Chip label="Error" size="small" color="error" />
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, fontSize: "0.8rem" }}>
                      {row.status === "success" && row.inviteResult && (
                        <Typography variant="caption" color="text.secondary">
                          DB: {row.inviteResult.databaseResult}
                          {row.inviteResult.isInternalUser && " • Internal user"}
                        </Typography>
                      )}
                      {row.status === "error" && row.errorMessage && (
                        <Typography variant="caption" color="error">
                          {row.errorMessage}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row.status === "pending" && (
                        <Button size="small" color="error" onClick={() => handleRemoveRow(i)} disabled={importing}>
                          Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="contained"
            fullWidth
            disabled={importing || !companyId || pendingCount === 0}
            onClick={handleImport}
            sx={{
              backgroundColor: "#0D2C40",
              "&:hover": { backgroundColor: "#009530" },
              padding: "12px",
              fontSize: "1rem",
              fontWeight: 600,
            }}
          >
            {importing
              ? `Importing… (${successCount}/${rows.length})`
              : importDone
              ? `Retry failed (${errorCount})`
              : `Import ${pendingCount} user${pendingCount !== 1 ? "s" : ""}`}
          </Button>
        </>
      )}

      {importDone && errorCount === 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          All {successCount} users imported successfully!
        </Alert>
      )}
      {importDone && errorCount > 0 && successCount > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {successCount} imported, {errorCount} failed. You can fix issues and retry.
        </Alert>
      )}

      {importDone && (
        <Button
          variant="outlined"
          fullWidth
          onClick={handleDone}
          sx={{ mt: 2 }}
        >
          Close
        </Button>
      )}
    </Box>
  );
};

export default BatchImportUsersDialog;
